import { translate, translateObject } from '@jsverse/transloco';

import { Constants } from 'app/constants/constants';
import { WidgetChartOptions } from 'app/modules/widget/components/widget-chart/widget-chart.component';
import { WidgetGridOptions } from 'app/modules/widget/components/widget-grid/widget-grid.component';
import { AbstractChartReport } from 'app/modules/widget/services/chart-report/chart-report-abstract/chart-report-abstract';
import { Dashboard, Widget } from 'app/providers/bridge-api/dashboard.service';
import { Template } from 'app/providers/dashboard-templates.service';
import { Field, FieldService } from 'app/providers/storage-api/field.service';

import { Utils } from 'app/utils/utils';
import { WidgetDataSource } from '../../../models';

export interface ValidityResult {
  disabled: boolean;
  hidden?: boolean;
  tooltip: string;
}

interface DatasourceField {
  name: string;
  type: string;
  isRating: boolean;
  isRedacted: boolean;
  isBinary: boolean;
}

export class DashboardValidityChecks {
  public static readonly clauseModelTypes = ['Intent', 'Effort', 'Emotion'];
  private static readonly validKeys = new Set([
    'docs',
    'documents',
    'topics',
    'entities',
    'phrases',
    'taxonomy',
    'themes',
    'metadata',
    'standard Fields',
    'createdAt',
  ]);

  public static dataSourceValidCheck(
    widget: Widget,
    report: AbstractChartReport,
    dataSource: WidgetDataSource,
    accountMetadata?: Field[],
    skipAnalyticItemCheck?,
    dashboard?: Dashboard
  ): ValidityResult {
    const translations = translateObject('validity-checks');

    if (
      dataSource.isSocial &&
      widget.payload.type !== 'sentimentPolarity' &&
      widget.payload.type !== 'grid' &&
      widget.payload.type !== 'breakdown' &&
      widget.payload.type !== 'numericMetadata' &&
      widget.payload.type !== 'sentimentScore' &&
      widget.payload.type !== 'powerScore' &&
      widget.payload.type !== 'powerScoreOverTime' &&
      widget.payload.type !== 'documents' &&
      widget.payload.type !== 'documentCount' &&
      widget.payload.type !== 'sentimentVolume' &&
      widget.payload.type !== 'journey'
    ) {
      return {
        tooltip: translations['datasource-social-not-supported'],
        disabled: true,
      };
    }

    if (!dataSource.isSocial && widget.payload.type.includes('powerScore')) {
      return {
        tooltip: translations['datasource-no-social'],
        disabled: true,
      };
    }

    //If predictive component
    if (
      widget.component === 'DashboardWidgetPredictiveComponent' ||
      (widget.component === 'DashboardWidgetImpactComponent' && widget.payload.isSpecial)
    ) {
      if (!dataSource.isDriverAnalysis) {
        return {
          tooltip: translations['datasource-no-kda'],
          disabled: true,
          hidden: true,
        };
      }
      //If new type of KDA but old type of predictive component
      if (
        dataSource.isDriverAnalysis &&
        dataSource.tags?.length &&
        widget?.payload.type !== 'predictor'
      ) {
        return {
          tooltip: translations['kda-newer'],
          disabled: true,
          hidden: true,
        };
      }

      //If old type of KDA but new type of predictive component
      if (
        dataSource.isDriverAnalysis &&
        !dataSource.tags?.length &&
        dashboard &&
        dashboard?.payload.type !== 'predictive'
      ) {
        return {
          tooltip: translations['kda-older'],
          disabled: true,
          hidden: true,
        };
      }
      return { tooltip: undefined, disabled: false };
    } else if (dataSource.isDriverAnalysis && dataSource.tags?.length) {
      return {
        tooltip: translations['kda-not-allowed'],
        disabled: true,
        hidden: true,
      };
    }

    if (!dataSource.isFinished && !dataSource.isAutoAnalyze) {
      return { tooltip: translations['datasource-is-analysing'], disabled: true };
    }

    if (this.isDocumentList(widget)) {
      return { tooltip: undefined, disabled: false };
    }

    //Dates
    if (
      report?.reportName.includes('OverTime') ||
      report?.reportName.toLocaleLowerCase().includes('sentimentversusvolume') ||
      widget.payload.dateInterval ||
      widget.payload.type === 'sentimentVolume'
    ) {
      if (!dataSource.hasDateData) {
        return { tooltip: translations['datasource-has-no-date'], disabled: true };
      }
    }

    // Impact
    if (!dataSource.hasTopics && widget.payload.type === 'impact') {
      return { tooltip: translations['datasource-has-no-topics'], disabled: true };
    }

    //Taxonomy
    if (report?.reportName.includes('taxonomyLevel') || widget.payload.type === 'taxonomyMap') {
      if (!dataSource.hasTaxonomy) {
        return { tooltip: translations['datasource-has-no-taxonomy'], disabled: true };
      }
      if (dataSource.sourceVersion < 7) {
        return {
          tooltip: translations['reanalyze-datasource-taxonomy'],
          disabled: true,
        };
      }

      return { tooltip: undefined, disabled: false };
    }

    //Journey
    if (widget.payload.type === 'journey' && !dataSource.isJourneyGroup) {
      return {
        tooltip: '',
        disabled: true,
        hidden: true,
      };
    }
    //IS a Journey widget being created and a datasource without date data is selected
    if (widget.payload.type === 'journey' && dataSource.isJourneyGroup && !dataSource.hasDateData) {
      return {
        tooltip: translations['datasource-has-no-date'],
        disabled: true,
        hidden: false,
      };
    }

    if (dataSource.sourceVersion > 1) {
      const analyticItemsUsed = [];

      if (widget.payload.analyticItem && !skipAnalyticItemCheck) {
        analyticItemsUsed.push({
          analyticItem: widget.payload.analyticItem,
          customFieldSelected: widget.payload.customFieldSelected,
          entityTypeFilter: widget.payload.entityTypeFilter as string,
        });
      }

      if (widget.payload.columnAnalyticItem) {
        analyticItemsUsed.push({
          analyticItem: widget.payload.columnAnalyticItem,
          customFieldSelected: widget.payload.customFieldSelected,
          entityTypeFilter: widget.payload.columnEntityTypeFilter as string,
        });
      }

      const analyticItemValidationResult = this.findAnalyticItemsForWidget(
        widget,
        dataSource,
        analyticItemsUsed,
        translations,
        accountMetadata
      );

      if (analyticItemValidationResult) {
        return analyticItemValidationResult;
      }

      for (const item of analyticItemsUsed) {
        if (item.analyticItem?.includes('sectionProperties')) {
          if (
            [
              'featureSentimentScoreOverTime',
              'featureSentimentScore',
              'documentSentimentPolarity',
              'documentSentimentPolarityTopFeatures',
            ].includes(report.reportName) &&
            !dataSource.hasSectionSentiment
          ) {
            return {
              tooltip: translate('validity-checks.datasource-has-no-section-sentiment', {
                item: item.analyticItem,
              }),
              disabled: true,
            };
          }
        }
        const hasValues = DashboardValidityChecks.dataSourceHasValuesFor(
          dataSource,
          item.analyticItem
        );
        if (hasValues === false) {
          let selectedItemName = item.analyticItem;

          if (FieldService.isSectionProperty(item.analyticItem)) {
            selectedItemName = FieldService.getSectionPropertyShortName(item.analyticItem);
          }

          if (item.customFieldSelected) {
            selectedItemName = FieldService.getFieldShortName(item.analyticItem);

            if (selectedItemName === item.analyticItem) {
              selectedItemName = translations['such-custom-field'];
            }
          }

          return {
            tooltip: translate('validity-checks.datasource-has-no-item', {
              item: selectedItemName,
            }),
            disabled: true,
          };
        }

        //Entities special case
        if (item.analyticItem === 'entities' && item.entityTypeFilter && dataSource.entityTypes) {
          const subtypes = item.entityTypeFilter.split(',');
          const missingTypes = subtypes.filter((et) => !dataSource.entityTypes.includes(et));

          if (missingTypes.length) {
            return {
              tooltip: translate('validity-checks.datasource-has-no-entities-of', {
                entityTypes: missingTypes.join(', '),
              }),
              disabled: true,
            };
          }
        }

        //Clause models special case
        if (item.analyticItem === 'topics' && item.entityTypeFilter) {
          const subtypes = item.entityTypeFilter.split(',');
          const missingClauseTypes = [];
          let hasNonClauseTypes = false;
          for (const subtype of subtypes) {
            //If the selected topics include types that aren't clause models return true.
            if (!this.clauseModelTypes.includes(subtype)) {
              hasNonClauseTypes = true;
            } else {
              if (
                !dataSource.clauseModelTypes ||
                (dataSource.clauseModelTypes && !dataSource.clauseModelTypes.includes(subtype))
              ) {
                missingClauseTypes.push(subtype);
              }
            }
          }
          if (!hasNonClauseTypes && missingClauseTypes.length > 0) {
            return {
              tooltip: translate('validity-checks.datasource-has-no-clause-models-of', {
                clauseModels: missingClauseTypes.join(', '),
              }),
              disabled: true,
            };
          }
        }
      }
    }

    return { tooltip: undefined, disabled: false };
  }

  private static findAnalyticItemsForWidget(
    widget: Widget,
    dataSource,
    analyticItemsUsed,
    translations,
    accountMetadata?
  ): void | {
    disabled: boolean;
    tooltip: string;
  } {
    switch (widget.payload.type) {
      case 'grid':
        if (accountMetadata) {
          const dataSourceFields = this.findDatasourceFields(
            dataSource,
            accountMetadata,
            'keyword',
            Constants.GRID_ALLOWED_STANDARD_FIELDS
          );
          if (!dataSourceFields.customFields.length && !dataSourceFields.standardFields.length) {
            return {
              disabled: true,
              tooltip: translations['datasource-has-no-keyword-nor-allowed'],
            };
          }
        }

        if (widget.payload.breakdownField) {
          const gridPayload = widget.payload as WidgetGridOptions;

          analyticItemsUsed.push({
            analyticItem: gridPayload.breakdownField,
            customFieldSelected: FieldService.isCustomField(gridPayload.breakdownField),
          });

          gridPayload.numericMetadataFields
            .filter((f) => f.fieldId !== 'sentimentScore')
            .forEach((f) =>
              analyticItemsUsed.push({
                analyticItem: f.fieldId,
                customFieldSelected: FieldService.isCustomField(f.fieldId),
              })
            );
        }
        break;
      case 'breakdown':
        if (accountMetadata) {
          const dataSourceFields = this.findDatasourceFields(
            dataSource,
            accountMetadata,
            'keyword'
          );
          if (
            !dataSourceFields.customFields.length &&
            !dataSourceFields.standardFields.length &&
            !dataSource.hasSections
          ) {
            return {
              disabled: true,
              tooltip: translations['datasource-has-no-keyword-nor-allowed'],
            };
          }
        }

        if (widget.payload.breakdownOptions) {
          const chartPayload = widget.payload as WidgetChartOptions;

          analyticItemsUsed.push({
            analyticItem: chartPayload.breakdownOptions.numericMetadataFieldId,
            customFieldSelected: FieldService.isCustomField(
              chartPayload.breakdownOptions.numericMetadataFieldId
            ),
          });
        }
        break;
      case 'numericMetadata':
        if (accountMetadata) {
          const dataSourceFields = this.findDatasourceFields(dataSource, accountMetadata, [
            'double',
            'long',
          ]);
          if (!dataSourceFields.customFields.length) {
            return {
              disabled: true,
              tooltip: translations['datasource-has-no-numeric'],
            };
          }
        }

        if (widget.payload.numericMetadataFields) {
          const chartPayload = widget.payload as WidgetChartOptions;

          chartPayload.numericMetadataFields.forEach((f) =>
            analyticItemsUsed.push({
              analyticItem: f.fieldId,
              customFieldSelected: FieldService.isCustomField(f.fieldId),
            })
          );
        }
        break;
      case 'donut':
        if (accountMetadata) {
          const dataSourceFields = this.findDatasourceFields(dataSource, accountMetadata, 'long');
          if (!dataSourceFields.customFields.length) {
            return {
              disabled: true,
              tooltip: translations['datasource-has-no-integer-rating'],
            };
          }
        }
        break;
      case 'gauge':
        if (accountMetadata) {
          const dataSourceFields = this.findDatasourceFields(dataSource, accountMetadata, 'rating');
          if (!dataSourceFields.customFields.length) {
            return {
              disabled: true,
              tooltip: translations['datasource-has-no-rating'],
            };
          }
        }
        break;
      case 'impact':
        if (!widget.payload.isSpecial) {
          analyticItemsUsed.push({
            analyticItem: widget.payload.ratingField,
            customFieldSelected: true,
          });
        }

        if (accountMetadata) {
          const dataSourceFields = this.findDatasourceFields(dataSource, accountMetadata, 'rating');
          if (!dataSourceFields.customFields.length) {
            return {
              disabled: true,
              tooltip: translations['datasource-has-no-rating'],
            };
          }
        }
        break;
      case 'featureScoring':
        if (!dataSource.hasTopics) {
          return {
            disabled: true,
            tooltip: translations['datasource-has-no-topics'],
          };
        }

        if (accountMetadata) {
          const dataSourceFields = this.findDatasourceFields(
            dataSource,
            accountMetadata,
            'keyword'
          );
          if (!dataSourceFields.customFields.length) {
            return {
              disabled: true,
              tooltip: translations['datasource-has-no-string'],
            };
          }
        }
        break;
      case 'sentimentAccuracy':
        if (accountMetadata) {
          const dataSourceFields = this.findDatasourceFields(
            dataSource,
            accountMetadata,
            'keyword'
          );
          if (!dataSourceFields.customFields.length) {
            return {
              disabled: true,
              tooltip: translations['datasource-has-no-string'],
            };
          }
        }
        break;
      case 'bigNumber':
        if (widget.payload.analyticItemTwo) {
          analyticItemsUsed.push({
            analyticItem: widget.payload.analyticItemTwo,
            customFieldSelected: widget.payload.metadataTwoCustomFieldSelected,
            entityTypeFilter: widget.payload.columnEntityTypeFilter as string,
          });
        }
        break;
      case 'focusAreas':
        if (!widget.payload.isSpecial) {
          analyticItemsUsed.push({
            analyticItem: widget.payload.ratingField,
            customFieldSelected: true,
          });
        }

        if (accountMetadata) {
          const dataSourceFields = this.findDatasourceFields(dataSource, accountMetadata, 'rating');
          if (!dataSourceFields.customFields.length) {
            return {
              disabled: true,
              tooltip: translations['datasource-has-no-rating'],
            };
          }
        }
        break;
      case 'journey':
        if (widget.payload.numericMetadataFields) {
          widget.payload.numericMetadataFields
            .filter((f) => f.fieldId !== 'sentimentScore')
            .forEach((f) =>
              analyticItemsUsed.push({
                analyticItem: f.fieldId,
                customFieldSelected: FieldService.isCustomField(f.fieldId),
              })
            );
        }
        break;
    }
  }

  private static findDatasourceFields(
    dataSource: WidgetDataSource,
    accountMetadata: Field[],
    fieldType?: string | string[],
    standardFields?: string[]
  ): { customFields: DatasourceField[]; standardFields: DatasourceField[] } {
    const customRes: DatasourceField[] = [];
    const standardRes: DatasourceField[] = [];

    const dsMetadataFields = accountMetadata
      .filter((f) => dataSource.customFields.includes(f.fieldName))
      .map((f) => ({
        name: f.fieldName,
        type: f.fieldType,
        isRating: f.metadata?.rating === true || f.metadata?.calculationType === 'binary',
        isRedacted: f.metadata?.redacted === true,
        isBinary: f.metadata?.calculationType === 'binary',
      }));

    const dsStandardFields = FieldService.STANDARD_FIELDS.filter((f) =>
      dataSource.standardFields?.includes(f.property)
    ).map((f) => {
      let type: string;
      switch (f.type) {
        case 'date':
          type = 'date';
          break;
        case 'string':
        case 'url':
          type = 'keyword';
          break;
        case 'number':
          type = 'long';
          break;
      }

      return { name: f.label, type, isRating: false, isRedacted: false, isBinary: false };
    });

    if (fieldType) {
      const filterFn = (f) => {
        if (typeof fieldType === 'string') {
          if (fieldType === 'rating') {
            return f.isRating;
          }
          return f.type === fieldType;
        }

        if (fieldType.includes('rating')) {
          return f.isRating || fieldType.includes(f.type);
        }
        // Include binary fields when checking for numeric types (double/long)
        if ((fieldType.includes('double') || fieldType.includes('long')) && f.isBinary) {
          return true;
        }
        return fieldType.includes(f.type);
      };

      customRes.push(...dsMetadataFields.filter(filterFn));
      standardRes.push(...dsStandardFields.filter(filterFn));
    }

    if (standardFields?.length) {
      standardRes.push(...dsStandardFields.filter((f) => standardFields.includes(f.name)));
    }

    return {
      customFields: customRes,
      standardFields: standardRes,
    };
  }

  public static dataSourceValidCheckDashboard(
    dashboard: Dashboard,
    dataSource: WidgetDataSource,
    template?: Template
  ): ValidityResult {
    const translations = translateObject('validity-checks');

    switch (dashboard.payload.type) {
      case 'predictive':
        if (!dataSource.isDriverAnalysis) {
          return { tooltip: translations['datasource-no-kda'], disabled: true };
        }
        if (dataSource.isDriverAnalysis && dataSource.tags?.length) {
          return {
            tooltip: translations['kda-newer'],
            disabled: true,
            hidden: true,
          };
        }
        break;
      case 'survey':
        {
          if (!dataSource.hasTaxonomy) {
            return { tooltip: translations['datasource-has-no-taxonomy'], disabled: true };
          }

          if (dataSource.sourceVersion < 7) {
            return {
              tooltip: translations['reanalyze-datasource-taxonomy'],
              disabled: true,
            };
          }
        }
        break;
    }

    if (!this.isTemplateValid(template, dataSource)) {
      return { tooltip: translations['template-requirements'], disabled: true };
    }

    return { tooltip: undefined, disabled: false };
  }

  private static isTemplateValid(template: Template, dataSource: WidgetDataSource): boolean {
    if (!template) {
      return true;
    }

    // v1 only one datasource slot
    const datasourceSlot = template.datasourceSlots?.[0];
    if (!datasourceSlot) {
      return true;
    }

    const checkRequirements = (req: Record<string, boolean>): boolean => {
      if (!req) {
        return true;
      }

      let result = true;
      Object.keys(req).forEach((k) => {
        result &&= req[k] === dataSource[k];
      });

      return result;
    };

    if (!checkRequirements(datasourceSlot.analysisRequirements)) {
      return false;
    }

    if (!checkRequirements(datasourceSlot.collectionRequirements)) {
      return false;
    }

    return true;
  }

  public static analyticTypeValidCheck(
    key: string,
    widget: Widget,
    widgetDataSource: WidgetDataSource,
    report: AbstractChartReport
  ): ValidityResult {
    if (!widget || widgetDataSource.sourceVersion <= 1) {
      return { tooltip: undefined, disabled: false };
    }

    const reportSupported = report.isAnalyticItemSupported(key);
    const reportNotSupportedtooltip = report.getAnalyticItemNotSupportedTooltip(key);
    let translatedKey = translate(`analytic-items.${key}`);
    if (translatedKey === `analytic-items.${key}`) {
      translatedKey = key;
    }

    if (widgetDataSource.isSocial) {
      if (
        key !== 'taxonomy' &&
        key !== 'phrases' &&
        key !== 'topics' &&
        key !== 'entities' &&
        key !== 'themes' &&
        key !== 'documents'
      ) {
        const tooltipText =
          reportNotSupportedtooltip ||
          translate('validity-checks.report-does-not-support-item', { item: translatedKey });
        return { tooltip: tooltipText, disabled: true };
      }
    }

    if (!reportSupported) {
      const tooltipText =
        reportNotSupportedtooltip ||
        translate('validity-checks.report-does-not-support-item', { item: translatedKey });
      return { tooltip: tooltipText, disabled: true };
    }

    const hasValues = DashboardValidityChecks.dataSourceHasValuesFor(widgetDataSource, key);
    if (hasValues === false) {
      // If the item is a taxonomy, then the analysis must be after version 7 (when the taxonomy was overhauled)
      if (
        key === 'taxonomy' &&
        widgetDataSource.sourceVersion < 7 &&
        widgetDataSource.hasTaxonomy
      ) {
        return {
          tooltip: translate('validity-checks.reanalyze-datasource-taxonomy'),
          disabled: true,
        };
      }
      return {
        tooltip: translate('validity-checks.item-not-present-in-datasource', {
          item: translatedKey,
        }),
        disabled: true,
      };
    }

    return { tooltip: undefined, disabled: false };
  }

  public static analyticSubTypeValidCheck(
    analyticItem: string,
    subType: string,
    fieldTypeMap: Map<string, string>,
    widget: Widget
  ): ValidityResult {
    if (analyticItem === 'metadata' && !fieldTypeMap.has(subType)) {
      return {
        tooltip: '',
        disabled: true,
      };
    }

    if (
      widget.payload.type === 'bigNumber' &&
      fieldTypeMap.get(subType) !== 'double' &&
      fieldTypeMap.get(subType) !== 'long'
    ) {
      return {
        tooltip: translate('validity-checks.only-numeric-metadata-supported'),
        disabled: true,
      };
    }

    return { tooltip: undefined, disabled: false };
  }

  public static dataSourceHasValuesFor(
    dataSource: WidgetDataSource,
    key: string | undefined
  ): boolean {
    let hasValues: boolean;
    switch (key) {
      case 'topics':
        hasValues = dataSource.hasTopics;
        break;
      case 'entities':
        hasValues = dataSource.hasEntities;
        break;
      case 'phrases':
        hasValues = dataSource.hasPhrases;
        break;
      case 'taxonomy':
        hasValues = dataSource.hasTaxonomy && dataSource.sourceVersion >= 7;
        break;
      case 'themes':
        hasValues = dataSource.hasThemes;
        break;
      case 'createdAt':
        hasValues = dataSource.hasDateData;
        break;
      case 'docs':
      case 'documents':
      case undefined:
        hasValues = true;
        break;
    }

    if (key && !DashboardValidityChecks.validKeys.has(key)) {
      const isStandard = dataSource.standardFields
        ? dataSource.standardFields.indexOf(key) !== -1
        : false;
      const isCustom = dataSource.customFields
        ? dataSource.customFields.indexOf(key) !== -1
        : false;

      hasValues = isStandard || isCustom;
    }

    if (key?.includes('sectionProperties')) {
      const property = FieldService.getSectionPropertyShortName(key);
      hasValues = dataSource.sectionPropertyTypes
        ? dataSource.sectionPropertyTypes
            // There is a little inconsistency in the API
            // - some times the property names are camelCase, some times they are UPPER_SNAKE_CASE
            .map((p) => Utils.toUpperSnakeCase(p))
            .indexOf(Utils.toUpperSnakeCase(property)) !== -1
        : false;
    }

    return hasValues;
  }

  public static isDocumentList(widget: Widget) {
    return widget.component === 'DashboardWidgetDocumentListComponent';
  }
}
